import type { Rules } from "odyssey-shared";
import type { PlaceSize, PlaceSource, SizeSource, WindowPlace } from "../../ui/kit/index.ts";
import type { BagSnapshot, BagSource } from "../bag/store.ts";
import type { Stance, StanceStore } from "../fight/stance.ts";
import type { HudProgress } from "../hud/store.ts";
import type { WalletSnapshot, WalletSource } from "../wallet/store.ts";

/**
 * What the HUD's tooltip reads besides the bar (`tooltip-view.tsx`): the XP bar's progress, the stance and the bag,
 * each optional on a HUD, and what stands in for one a HUD does not have.
 */

/**
 * What the XP bar's tooltip reads: the player's progress and flag byte (P2.4-T8).
 */
export interface XpSubject {
  readonly progress: HudProgress;
  readonly flag: number;
}

/**
 * What the stance button's tooltip reads (P2.2-T13): the stance and a way to hear it change.
 */
export type StanceSubject = Pick<StanceStore, "subscribe" | "getSnapshot">;

/**
 * What a bag slot's item card reads (P2.8-T11, P2.8-T46): the bag, the rules its items are drawn by (the bar's unless
 * given), whether a consumable's heal is halved now (in combat with a player, the `vitals` bit `InPvpCombat`, ADR-091's amendment of 2026-10-08), which the card says the consequence of, and where the window's place and its size are kept (`place` and `size`, the page's own for Inventory unless
 * given), since the card stands beside the window as drawn; and the wallet its footer draws (`wallet`, P2.8-T67), which a
 * wallet mark's label reads.
 */
export interface BagSubject {
  readonly source: BagSource;
  readonly rules?: Rules | undefined;
  readonly potionsHalved: boolean;
  readonly place?: PlaceSource | undefined;
  readonly size?: SizeSource | undefined;
  readonly wallet?: WalletSource | undefined;
}

/**
 * No kept place, for a HUD with no bag.
 *
 * @returns Null.
 */
export const NO_PLACE = (): WindowPlace | null => null;

/**
 * No kept size, for a HUD with no bag.
 *
 * @returns Null.
 */
export const NO_SIZE = (): PlaceSize | null => null;

/**
 * No bag, for a HUD with none.
 *
 * @returns Null.
 */
export const NO_BAG = (): BagSnapshot | null => null;

/**
 * Subscribes to nothing, for a HUD with no stance.
 *
 * @returns A function that unsubscribes from nothing.
 */
export const NO_SUBSCRIBE = (): (() => void) => () => undefined;

/**
 * No stance, for a HUD with none.
 *
 * @returns Null.
 */
export const NO_STANCE = (): Stance | null => null;

/**
 * No wallet, for a HUD whose Inventory window draws none.
 *
 * @returns Null.
 */
export const NO_WALLET = (): WalletSnapshot => null;
